import React, { useCallback, useEffect, useState } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import {
  AppBar,
  Box,
  Chip,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import LockIcon from '@mui/icons-material/Lock';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import AddIcon from '@mui/icons-material/Add';
import BugReportIcon from '@mui/icons-material/BugReport';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import LogoutIcon from '@mui/icons-material/Logout';
import MenuIcon from '@mui/icons-material/Menu';
import FolderSharedIcon from '@mui/icons-material/FolderShared';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import LightModeIcon from '@mui/icons-material/LightMode';
import ScienceIcon from '@mui/icons-material/Science';
import { useAuth } from '../../hooks/useAuth';
import { useThemeMode } from '../../hooks/ThemeContext';
import { sandboxService } from '../../services/sandboxService';
import { SandboxFindingListItem, SharedAuditListItem, SharedFindingListItem } from '../../types';
import { getKritikalitaetHexColor } from '../../utils/severity';

export const RESTRICTED_DRAWER_WIDTH = 280;

/**
 * Layout der eingeschraenkten Sitzung (Restricted-Login).
 *
 * Bewusst getrennt vom MainLayout: keine globale Suche, keine Navigation in andere
 * Bereiche. Linke Spalte: eigene Sandbox-Findings, klar getrennt davon der
 * Nur-Lesen-Bereich "Freigegeben".
 */
const RestrictedLayout: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const { mode, toggleTheme } = useThemeMode();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [mobileOpen, setMobileOpen] = useState(false);

  const [findings, setFindings] = useState<SandboxFindingListItem[]>([]);
  const [sharedAudits, setSharedAudits] = useState<SharedAuditListItem[]>([]);
  const [sharedFindings, setSharedFindings] = useState<SharedFindingListItem[]>([]);

  const reload = useCallback(async () => {
    const [f, a, s] = await Promise.all([
      sandboxService.list(false).catch(() => []),
      sandboxService.shared.audits().catch(() => []),
      sandboxService.shared.findings().catch(() => []),
    ]);
    setFindings(f);
    setSharedAudits(a);
    setSharedFindings(s);
  }, []);

  useEffect(() => {
    reload();
  }, [reload, location.pathname]);

  useEffect(() => {
    const handler = () => reload();
    window.addEventListener('sandbox-findings-changed', handler);
    return () => window.removeEventListener('sandbox-findings-changed', handler);
  }, [reload]);

  const isDark = theme.palette.mode === 'dark';
  const accent = theme.palette.primary.main;
  const bgSidebar = isDark ? '#1c203b' : '#ffffff';
  const hoverBg = isDark ? '#2c3358' : '#eef1f5';
  const sidebarText = isDark ? '#b0bec5' : '#3a4a5a';
  const sidebarLabel = isDark ? '#556677' : '#8899aa';
  const sidebarIcon = isDark ? '#667788' : '#6b7c8d';
  const warn = theme.palette.warning.main;

  const go = (path: string) => {
    navigate(path);
    if (isMobile) setMobileOpen(false);
  };
  const isActive = (path: string) => location.pathname === path || location.pathname.startsWith(`${path}/`);

  const navItemSx = {
    borderRadius: 2,
    mb: 0.3,
    py: 0.75,
    '&.Mui-selected': {
      bgcolor: hoverBg,
      borderLeft: `3px solid ${accent}`,
      '& .MuiListItemIcon-root': { color: accent },
      '& .MuiListItemText-primary': { color: accent, fontWeight: 600 },
    },
    '&:hover': { bgcolor: hoverBg },
  };
  const sectionSx = {
    fontSize: '0.65rem',
    fontWeight: 700,
    color: sidebarLabel,
    letterSpacing: '0.1em',
    textTransform: 'uppercase',
    px: 2.5,
    pt: 2,
    pb: 0.5,
    display: 'flex',
    alignItems: 'center',
    gap: 0.75,
  } as const;
  const textProps = { fontSize: '0.85rem', fontWeight: 500, color: sidebarText, noWrap: true };

  const drawer = (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Box sx={{ px: 2.5, py: 2, display: 'flex', alignItems: 'center', gap: 1.25 }}>
        <Box component="img" src="/stark_logo.png" alt="STARK IT-Security" sx={{ height: 40, objectFit: 'contain' }} />
      </Box>
      <Box sx={{ px: 2, pb: 1.5 }}>
        <Chip
          icon={<LockIcon sx={{ fontSize: '1rem !important' }} />}
          label="Eingeschränkter Zugang"
          size="small"
          sx={{
            width: '100%',
            justifyContent: 'flex-start',
            fontWeight: 700,
            bgcolor: `${warn}22`,
            color: warn,
            border: `1px solid ${warn}66`,
          }}
        />
      </Box>
      <Divider />

      <Box sx={{ flex: 1, overflowY: 'auto' }}>
        <Typography sx={sectionSx}>
          <ScienceIcon sx={{ fontSize: '0.9rem' }} /> Isolierte Umgebung
        </Typography>
        <List sx={{ px: 1.5 }}>
          <ListItemButton selected={location.pathname === '/sandbox'} onClick={() => go('/sandbox')} sx={navItemSx}>
            <ListItemIcon sx={{ minWidth: 36, color: sidebarIcon }}><BugReportIcon fontSize="small" /></ListItemIcon>
            <ListItemText primary="Meine Findings" primaryTypographyProps={textProps} />
          </ListItemButton>
          <ListItemButton
            selected={location.pathname === '/sandbox/findings/neu'}
            onClick={() => go('/sandbox/findings/neu')}
            sx={navItemSx}
          >
            <ListItemIcon sx={{ minWidth: 36, color: sidebarIcon }}><AddIcon fontSize="small" /></ListItemIcon>
            <ListItemText primary="Neues Finding" primaryTypographyProps={textProps} />
          </ListItemButton>
          {findings.map((f) => (
            <ListItemButton
              key={f.id}
              selected={isActive(`/sandbox/findings/${f.id}`)}
              onClick={() => go(`/sandbox/findings/${f.id}`)}
              sx={{ ...navItemSx, pl: 3 }}
            >
              <Box
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  bgcolor: getKritikalitaetHexColor(f.kritikalitaet || ''),
                  mr: 1.5,
                  flexShrink: 0,
                }}
              />
              <ListItemText
                primary={f.titel || 'Ohne Titel'}
                primaryTypographyProps={{ ...textProps, fontSize: '0.8rem' }}
                title={f.titel}
              />
            </ListItemButton>
          ))}
          {findings.length === 0 && (
            <Typography sx={{ px: 2.5, py: 0.5, fontSize: '0.75rem', color: sidebarLabel }}>
              Noch keine Findings
            </Typography>
          )}
        </List>

        <Divider sx={{ mx: 1.5, mt: 1 }} />
        <Typography sx={sectionSx}>
          <VisibilityIcon sx={{ fontSize: '0.9rem' }} /> Freigegeben (nur lesen)
        </Typography>
        <List sx={{ px: 1.5 }}>
          {sharedAudits.map((a) => (
            <ListItemButton
              key={`a-${a.id}`}
              selected={isActive(`/sandbox/shared/audits/${a.id}`)}
              onClick={() => go(`/sandbox/shared/audits/${a.id}`)}
              sx={navItemSx}
            >
              <ListItemIcon sx={{ minWidth: 36, color: sidebarIcon }}><FolderSharedIcon fontSize="small" /></ListItemIcon>
              <ListItemText
                primary={a.name}
                secondary={`${a.findings_count} Findings`}
                primaryTypographyProps={textProps}
                secondaryTypographyProps={{ fontSize: '0.7rem' }}
              />
            </ListItemButton>
          ))}
          {sharedFindings.map((f) => (
            <ListItemButton
              key={`f-${f.id}`}
              selected={isActive(`/sandbox/shared/findings/${f.id}`)}
              onClick={() => go(`/sandbox/shared/findings/${f.id}`)}
              sx={navItemSx}
            >
              <ListItemIcon sx={{ minWidth: 36, color: sidebarIcon }}><LockOpenIcon fontSize="small" /></ListItemIcon>
              <ListItemText
                primary={f.titel}
                secondary={f.audit_name}
                primaryTypographyProps={textProps}
                secondaryTypographyProps={{ fontSize: '0.7rem', noWrap: true }}
              />
            </ListItemButton>
          ))}
          {sharedAudits.length === 0 && sharedFindings.length === 0 && (
            <Typography sx={{ px: 2.5, py: 0.5, fontSize: '0.75rem', color: sidebarLabel }}>
              Keine Freigaben
            </Typography>
          )}
        </List>
      </Box>

      <Divider />
      <List sx={{ px: 1.5, py: 1 }}>
        <ListItemButton selected={isActive('/sandbox/api-docs')} onClick={() => go('/sandbox/api-docs')} sx={navItemSx}>
          <ListItemIcon sx={{ minWidth: 36, color: sidebarIcon }}><MenuBookIcon fontSize="small" /></ListItemIcon>
          <ListItemText primary="API-Dokumentation" primaryTypographyProps={textProps} />
        </ListItemButton>
      </List>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: 'background.default' }}>
      <Drawer
        variant={isMobile ? 'temporary' : 'permanent'}
        open={isMobile ? mobileOpen : true}
        onClose={() => setMobileOpen(false)}
        ModalProps={{ keepMounted: true }}
        sx={{
          width: { xs: 0, md: RESTRICTED_DRAWER_WIDTH },
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: RESTRICTED_DRAWER_WIDTH,
            boxSizing: 'border-box',
            bgcolor: bgSidebar,
            borderRight: `1px solid ${theme.palette.divider}`,
          },
        }}
      >
        {drawer}
      </Drawer>

      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          width: { md: `calc(100% - ${RESTRICTED_DRAWER_WIDTH}px)` },
          ml: { md: `${RESTRICTED_DRAWER_WIDTH}px` },
          bgcolor: 'background.paper',
          color: 'text.primary',
          borderBottom: `1px solid ${theme.palette.divider}`,
        }}
      >
        <Toolbar sx={{ gap: 1 }}>
          {isMobile && (
            <IconButton edge="start" onClick={() => setMobileOpen(true)}><MenuIcon /></IconButton>
          )}
          <LockIcon sx={{ color: warn }} fontSize="small" />
          <Typography variant="subtitle1" sx={{ fontWeight: 600, flexGrow: 1 }} noWrap>
            Isolierte Umgebung – {user?.vorname} {user?.nachname}
            <Typography component="span" variant="body2" sx={{ color: 'text.secondary', ml: 1 }}>
              ({user?.email})
            </Typography>
          </Typography>
          <Tooltip title={mode === 'dark' ? 'Helles Design' : 'Dunkles Design'}>
            <IconButton onClick={toggleTheme} size="small">
              {mode === 'dark' ? <LightModeIcon fontSize="small" /> : <DarkModeIcon fontSize="small" />}
            </IconButton>
          </Tooltip>
          <Tooltip title="Abmelden">
            <IconButton onClick={() => logout()} size="small" data-testid="restricted-logout">
              <LogoutIcon sx={{ color: '#ef5350' }} fontSize="small" />
            </IconButton>
          </Tooltip>
        </Toolbar>
      </AppBar>

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: { xs: 1.5, sm: 2, md: 3 },
          width: { xs: '100%', md: `calc(100% - ${RESTRICTED_DRAWER_WIDTH}px)` },
          minWidth: 0,
        }}
      >
        <Toolbar />
        <Outlet />
      </Box>
    </Box>
  );
};

export default RestrictedLayout;
